import { readCodedError } from '~/api/codedError';
import { API_BASE_URL } from '~/config';
import type { components } from '~/types/api';

export type Tenant = components['schemas']['Tenant'];
export type OfficeStatus = components['schemas']['OfficeStatusEnum'];

export const TENANT_UNKNOWN = 'TENANT_UNKNOWN';

/** Resposta do `/api/tenant/` que não é um escritório: `status` HTTP e o `code` do corpo, se houver. */
export class TenantLookupError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, code: string | null) {
    super(`Consulta do escritório recusada (${status}${code ? ` ${code}` : ''}).`);
    this.name = 'TenantLookupError';
    this.status = status;
    this.code = code;
  }
}

/** Verdadeiro só para o 404 `TENANT_UNKNOWN`: nenhum escritório ativo neste endereço. */
export function isUnknownTenant(error: unknown): boolean {
  return (
    error instanceof TenantLookupError && error.status === 404 && error.code === TENANT_UNKNOWN
  );
}

function isTenant(body: unknown): body is Tenant {
  return (
    typeof body === 'object' &&
    body !== null &&
    'slug' in body &&
    typeof body.slug === 'string' &&
    'hostname' in body &&
    typeof body.hostname === 'string'
  );
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * `GET /api/tenant/?host=`: o escritório dono do endereço, antes do login.
 *
 * Usa `fetch` e não o `apiClient`: a rota é pública, e sem o `Authorization` do interceptor a
 * requisição é "simples" para o CORS (sem preflight antes da primeira tela) e o
 * `Cache-Control: public` do backend vale no cache HTTP do navegador. Erro de rede sobe como está
 * (`TypeError`); resposta que não é escritório sobe como `TenantLookupError`, inclusive um 200
 * que não seja JSON (a API apontada para a própria origem devolve o `index.html`).
 */
export async function fetchTenant(host: string): Promise<Tenant> {
  const query = new URLSearchParams({ host });
  const response = await fetch(`${API_BASE_URL}/api/tenant/?${query}`, {
    headers: { Accept: 'application/json' },
  });
  const body = await readJson(response);
  if (response.ok && isTenant(body)) {
    return body;
  }
  throw new TenantLookupError(response.status, readCodedError(body)?.code ?? null);
}
